import type { CommunityBoard } from "./types"

// ── Section ID ↔ Board slug mapping ──
// Keys are backend section IDs (string), values are frontend board slugs.

export const SECTION_ID_TO_SLUG: Record<string, string> = {
  "1": "quant-trading",
  "2": "creative-lab",
  "3": "code-planet",
  "4": "marketing-hq",
  "5": "efficiency-dojo",
  "6": "life-manager",
  "7": "type-tool",
  "8": "type-agent",
  "9": "type-agent-team",
  "10": "type-skill",
  "11": "type-discussion",
  "12": "newbie-village",
}

export const SLUG_TO_SECTION_ID: Record<string, string> = Object.fromEntries(
  Object.entries(SECTION_ID_TO_SLUG).map(([id, slug]) => [slug, id])
)

/** Convert backend sectionId to frontend board slug */
export function sectionIdToSlug(sectionId: string): string {
  return SECTION_ID_TO_SLUG[sectionId] ?? `section-${sectionId}`
}

/** Convert frontend board slug to backend sectionId */
export function slugToSectionId(slug: string): string {
  return SLUG_TO_SECTION_ID[slug] ?? "11"
}

/** All community boards — 6 scenario + 4 type + 1 special */
export const COMMUNITY_BOARDS: CommunityBoard[] = [
  /* ── 场景类 (Scenario) ── */
  {
    slug: "quant-trading",
    name: "量化交易所",
    slogan: "数据会说话，Agent 帮你听懂",
    description: "股票、期货、基金的 AI 打法全在这里。分享量化策略 Workflow、行情播报 Agent、选股 Skill——让数字替你打工。",
    icon: "TrendingUp",
    category: "scenario",
    postCount: 342,
    color: "emerald",
  },
  {
    slug: "creative-lab",
    name: "创作实验室",
    slogan: "灵感 × AI = 你根本停不下来",
    description: "写小说、写剧本、写爆款文章、做播客脚本。从选题到成稿，从爆款标题到角色世界观构建，AI 辅助创作的正确姿势。",
    icon: "Pen",
    category: "scenario",
    postCount: 518,
    color: "pink",
  },
  {
    slug: "code-planet",
    name: "代码星球",
    slogan: "能跑的代码，才是好代码",
    description: "程序员的 AI 军火库。Debug Agent、代码审查 Workflow、文档自动生成 Skill、需求转代码一条龙。",
    icon: "Code",
    category: "scenario",
    postCount: 726,
    color: "blue",
  },
  {
    slug: "marketing-hq",
    name: "营销作战室",
    slogan: "爆款不是撞出来的，是设计出来的",
    description: "品牌文案、投放策略、竞品分析、用户洞察——营销人的 AI 工作台。",
    icon: "Megaphone",
    category: "scenario",
    postCount: 284,
    color: "orange",
  },
  {
    slug: "efficiency-dojo",
    name: "效率道场",
    slogan: "学得快不是天赋，是工具选对了",
    description: "考证备考、语言学习、知识管理、论文调研。把碎片时间变成复利。",
    icon: "BookOpen",
    category: "scenario",
    postCount: 461,
    color: "cyan",
  },
  {
    slug: "life-manager",
    name: "生活管家站",
    slogan: "AI 搞定琐事，你负责享受生活",
    description: "旅行规划、健康打卡、购物比价、家庭记账、育儿备忘。",
    icon: "Home",
    category: "scenario",
    postCount: 193,
    color: "amber",
  },

  /* ── 类型类 (Type) — maps to AbilityType ── */
  {
    slug: "type-tool",
    name: "Tool 工具箱",
    slogan: "好工具，让 Agent 如虎添翼",
    description: "API 接口、数据源、外部服务连接器——Agent 的双手，连接真实世界的桥梁。",
    icon: "Wrench",
    category: "type",
    postCount: 438,
    color: "amber",
    abilityType: "tool",
  },
  {
    slug: "type-agent",
    name: "Agent 广场",
    slogan: "不只是聊天机器人，是真的能干活",
    description: "各种身怀绝技的 AI 智能体——写合同的、做简报的、练面试的、跑数据的。",
    icon: "Bot",
    category: "type",
    postCount: 567,
    color: "blue",
    abilityType: "agent",
  },
  {
    slug: "type-agent-team",
    name: "Agent 团队",
    slogan: "一个人走得快，一群 Agent 走得远",
    description: "多 Agent 协作编排，让智能体组队完成复杂任务。团队配置、角色分工、协作流程全在这里。",
    icon: "Users",
    category: "type",
    postCount: 213,
    color: "red",
    abilityType: "agent-team",
  },
  {
    slug: "type-skill",
    name: "Skill 集市",
    slogan: "一个 Skill，解决一个痛点",
    description: "翻译、摘要、情感分析、格式转换、数据清洗……原子能力宝库，即插即用。",
    icon: "Zap",
    category: "type",
    postCount: 892,
    color: "violet",
    abilityType: "skill",
  },
  {
    slug: "type-discussion",
    name: "谈天说地",
    slogan: "技术之外，聊聊生活",
    description: "闲聊吐槽、交友求助、行业八卦、职场心得——不聊 AI 的时候，我们也是有趣的人。",
    icon: "MessageCircle",
    category: "type",
    postCount: 376,
    color: "gray",
    abilityType: "discussion",
  },

  /* ── 特色类 (Special) ── */
  {
    slug: "newbie-village",
    name: "新手村",
    slogan: "每个大神，都从这里出发",
    description: "入门教程、友好氛围、专为小白设计的入门 Workflow 合集。",
    icon: "Sprout",
    category: "special",
    postCount: 256,
    color: "lime",
  },
]

/** Get board by slug */
export function getBoardBySlug(slug: string): CommunityBoard | undefined {
  return COMMUNITY_BOARDS.find((b) => b.slug === slug)
}

/** Get boards by category */
export function getBoardsByCategory(category: CommunityBoard["category"]): CommunityBoard[] {
  return COMMUNITY_BOARDS.filter((b) => b.category === category)
}

/** Type boards — used for sub-filter chips */
export const TYPE_BOARDS = COMMUNITY_BOARDS.filter((b) => b.category === "type")
